import { IBaseline } from '@hooks/queries/anomalyDetection/anomalies.models';
import type { BBox } from '@turf/turf';
import { bbox } from '@turf/turf';
import dayjs from 'dayjs';
import { OperationsTilesApi, RemoteSensingApi } from '../../../../core/api';
import type { ImageAOI, SceneGroup } from '../../../../core/models/imageAssets';
import type { ImageType } from '../../../../core/models/metadata';
import { CWO_PALETTE_INDEX, RasterType } from '../../../../core/models/metadata';
import type { ColorMaps } from '../../../../core/models/user';
import type { Field } from '../../../../state/regions/region.models';
import { FieldImageDownloadOption } from '../Overlays/ScenePicker/common/AssetList/AssetListItem/assetListItem.util';

export const DEFAULT_RASTER_SIZE = 1024;

export const RETINA_CONFIG =
  'only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen  and (min-device-pixel-ratio: 1.3), only screen and (min-resolution: 1.3dppx)';

export const isRetinaDisplay = () =>
  window.matchMedia(RETINA_CONFIG)?.matches || window.devicePixelRatio > 1;

export interface IImagePreferences {
  subProduct: ImageType;
  palette?: ColorMaps;
  isMapTile?: boolean;
  variability?: number;
  field?: Field | null;
  isSideMap?: boolean;
  rasterType?: RasterType;
  fileExtension?: FieldImageDownloadOption;
  choroplethTiles?: boolean;
  customBbox?: BBox;
  onlyVisible?: boolean;
  rasterResolution?: number;
  highDPI?: boolean;
}

export const getFormattedSubProduct = (
  subProduct: ImageType,
  extension?: FieldImageDownloadOption
) => {
  if (extension === FieldImageDownloadOption.TIFF_RGB) return 'rgb';
  return subProduct;
};

export const getFormattedFileExtension = (extension?: FieldImageDownloadOption) => {
  switch (extension) {
    case FieldImageDownloadOption.TIFF_RGB:
    case FieldImageDownloadOption.TIFF_RAW:
      return FieldImageDownloadOption.TIFF;
    case undefined:
      return FieldImageDownloadOption.PNG;
    default:
      return extension;
  }
};

export const getRasterType = (subProduct: ImageType | 'ndvi', palette?: ColorMaps) => {
  if (subProduct === 'rgb' || subProduct === 'nir') {
    return 'truecolor';
  } else if (palette) {
    const selectedPaletteOption = palette.name.replace(/ /g, '_');
    return RasterType[selectedPaletteOption];
  } else {
    return 'falsecolor';
  }
};

export const getFieldBoundingBoxParams = (field: Field, customBbox?: BBox) => {
  const [upper_left_lon, lower_right_lat, lower_right_lon, upper_left_lat] = bbox(
    field.geometry
  ).map((coordinate) => coordinate.toString());

  const boundingBoxParams = new URLSearchParams({
    upper_left_lon,
    lower_right_lat,
    lower_right_lon,
    upper_left_lat,
  }).toString();

  if (customBbox) {
    const customBboxParams = new URLSearchParams({
      upper_left_lon: customBbox[0].toString(),
      lower_right_lat: customBbox[1].toString(),
      lower_right_lon: customBbox[2].toString(),
      upper_left_lat: customBbox[3].toString(),
    }).toString();
    return `?${customBboxParams}`;
  }

  return `?${boundingBoxParams}`;
};

const getMapTileURL = (isChoropleth?: boolean, onlyVisible?: boolean) => {
  if (isChoropleth) {
    return '?mean_mode=true';
  }
  if (onlyVisible) {
    return '?options=only_visible';
  }
  return '?';
};

const getFieldImageURL = (field: Field, customBbox?: BBox, rasterType?: RasterType) => {
  if (rasterType === RasterType.RAW) {
    return getFieldBoundingBoxParams(field, customBbox);
  }
  return getFieldBoundingBoxParams(field, customBbox) + `&raster_size=${DEFAULT_RASTER_SIZE}`;
};

export const getRemoteSensingImgURL = (
  image_id: string,
  {
    subProduct,
    palette,
    isMapTile,
    variability,
    field,
    rasterType,
    fileExtension,
    choroplethTiles,
    onlyVisible,
    customBbox,
    rasterResolution = 256,
    highDPI,
  }: IImagePreferences
) => {
  const formattedSubProduct = getFormattedSubProduct(subProduct, fileExtension);

  const formattedRasterType = rasterType ?? getRasterType(formattedSubProduct, palette);
  const shouldUseRawType = fileExtension === FieldImageDownloadOption.TIFF && 'raw';

  const extension = getFormattedFileExtension(fileExtension);

  const mapTileEndpoint = `/images/${image_id}/${formattedSubProduct}/${
    shouldUseRawType || formattedRasterType
  }/tiles_highdpi/${rasterResolution}/{z}/{x}/{y}${highDPI ? '@2x' : ''}.${extension}`;

  const staticImageEndpoint = `/images/${image_id}/${formattedSubProduct}/${
    shouldUseRawType || formattedRasterType
  }/${extension}`;

  const remoteSensingAPIDomain = import.meta.env.VITE_APP_REMOTE_SENSING;
  const endpointToBeUsed = isMapTile ? mapTileEndpoint : staticImageEndpoint;
  const url = `${remoteSensingAPIDomain}${endpointToBeUsed}`;

  if (variability) {
    return url + `?variability=${variability}`;
  }

  if (isMapTile) {
    return url + getMapTileURL(choroplethTiles, onlyVisible);
  }

  if (field) {
    return url + getFieldImageURL(field, customBbox, rasterType);
  }

  return url;
};

// Composite (property-level) tiles come from CWO when the image has a tilePath and no AOI,
// otherwise from the Remote Sensing tiler. Shared by CompositeTiles and ComparisonTiles so
// both resolve identical URLs for the same image/imageType.
export const getCompositeTileUrl = ({
  image,
  imageType,
  palette,
  retina,
}: {
  image: ImageAOI;
  imageType: ImageType;
  palette: ColorMaps;
  retina: boolean;
}) => {
  const shouldUseRemoteSensingTiles = Boolean(image?.aoi?.uid);
  return !shouldUseRemoteSensingTiles && image.tilePath
    ? getCWOTileUrl({ image, imageType, palette })
    : getRemoteSensingImgURL(image.id, {
        subProduct: imageType,
        palette,
        isMapTile: true,
        rasterResolution: 512,
        highDPI: retina,
      });
};

export const getBaselineTilesURL = (baseline: IBaseline, scene: SceneGroup) => {
  const endpoint = import.meta.env.VITE_APP_ANOMALY_DETECTION_BASELINE_ENDPOINT;

  const imageId = baseline.file_url.split('/').find((substring) => substring.includes('__')) ?? '';

  const paramsObj = {
    id: imageId,
    date: dayjs(scene.referenceImage.acquiredAt).format('YYYY-MM-DD'),
    colormap: 'ndvi_img_falsecolor',
    z: '{z}',
    x: '{x}',
    y: '{y}',
  };

  const params = Object.keys(paramsObj).reduce((acc, key, index) => {
    acc = acc + `${index > 0 ? '&' : ''}${key}=${paramsObj[key]}`;

    return acc;
  }, '');

  return endpoint + params;
};

export const fetchImage = async (
  url: string,
  fileExtension: FieldImageDownloadOption = FieldImageDownloadOption.PNG
) => {
  return await RemoteSensingApi.get(url, {
    responseType: 'arraybuffer',
  }).then((response) => {
    const blob = new Blob([new Uint8Array(response.data)], { type: `image/${fileExtension}` });
    return URL.createObjectURL(blob);
  });
};

export const fetchCWOImage = async (url: string) => {
  return await OperationsTilesApi.get(url, {
    responseType: 'arraybuffer',
  }).then((response) => {
    const blob = new Blob([new Uint8Array(response.data)], { type: 'image/png' });
    return URL.createObjectURL(blob);
  });
};

export const isCWOUrl = (url: string) => url.includes(import.meta.env.VITE_APP_OPERATIONS_API_URL);

export const fetchFieldImage = (url: string) =>
  isCWOUrl(url) ? fetchCWOImage(url) : fetchImage(url);

const getFormattedImageType = (imageType: string) => {
  switch (imageType) {
    case 'rgb':
      return 'visible';
    case 'shadow_corrected_drymatter':
      return 'drymatter';
    default:
      return imageType;
  }
};

const CWO_HISTOGRAM_PALETTE_ID = 34;

export const getCWOStaticImageUrl = ({
  tilePath,
  imageType,
  field,
}: {
  tilePath: string;
  imageType: ImageType;
  field: Field;
}) => {
  const domain = import.meta.env.VITE_APP_OPERATIONS_API_URL;
  const path = tilePath
    .replace('{index}', getFormattedImageType(imageType))
    .replace('field_groups', 'field_group')
    .replace('fields', 'field');

  const [minX, minY, maxX, maxY] = bbox(field.geometry);

  return `${domain}/${path}/${DEFAULT_RASTER_SIZE}x${DEFAULT_RASTER_SIZE}.png?palette_id=${CWO_HISTOGRAM_PALETTE_ID}&max_x=${maxX}&max_y=${maxY}&min_x=${minX}&min_y=${minY}`;
};

export const getCWOTileUrl = ({
  image,
  imageType,
  palette,
}: {
  image: ImageAOI;
  imageType: ImageType;
  palette: ColorMaps;
}) => {
  const domain = import.meta.env.VITE_APP_OPERATIONS_API_URL;

  const tilePath = image.tilePath?.replace(/{index}|ndvi/g, getFormattedImageType(imageType));

  const paletteName = imageType === 'rgb' ? 'SMS' : palette.name;

  const paletteId =
    imageType === 'shadow_corrected_drymatter' || image.source === 'drone'
      ? ''
      : `?palette_id=${CWO_PALETTE_INDEX[paletteName]}`;

  const isRetinaDisplay = window.matchMedia(RETINA_CONFIG)?.matches || window.devicePixelRatio > 1;
  const mapParameters = `512/{z}/{x}/{y}${isRetinaDisplay ? '@2x' : ''}.png`;

  return `${domain}/${tilePath}/${mapParameters}${paletteId}`;
};
